import { queriesPayrolls } from "@query";
import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";

import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import {
  PAYROLL_RUNS_HISTORY_STATUSES,
  PAYROLL_RUNS_TYPE
} from "../../constants";
import { selectorPayrollRunSummary } from "../../selectors/selectorPayrollRunSummary";
import { PayrollRunsYear } from "../../types";
import { getPayrollRunYearDateRange } from "../../utils";

import { TablePayrolls } from "./TablePayrolls";
import { buildPayrollsFiltersConfig } from "./filters/config";
import { PAYROLLS_CYCLE_FILTER_ALL, PayrollsFilter } from "./filters/types";

type Props = {
  year: PayrollRunsYear;
  onRowOpen: (id: string) => void;
};

export const FilterableTablePayrolls: React.FC<Props> = props => {
  const { urlQuery, setUrlQuery } = useUrlQuery<PayrollsFilter>();
  const filtersConfig = useMemo(() => buildPayrollsFiltersConfig(), []);

  const rawCycle = urlQuery.filter.cycleType;
  const cycleType =
    rawCycle && rawCycle !== PAYROLLS_CYCLE_FILTER_ALL ? rawCycle : undefined;

  // Server-paginated + server-filtered: listPayrolls accepts page + filter (no
  // sort). Year (payDate) and the history status set are the base filter; the
  // cycle radio composes on top. No client-side filter/sort over flatData.

  const query = queriesPayrolls.listPayrolls.usePaginatedQuery({
    filter: {
      type: { eq: PAYROLL_RUNS_TYPE },
      payDate: getPayrollRunYearDateRange(props.year),
      status: { anyOf: PAYROLL_RUNS_HISTORY_STATUSES },
      ...(cycleType ? { cycleType: { anyOf: [cycleType] } } : {})
    }
  });

  const payrollRuns = (query.flatData ?? []).map(selectorPayrollRunSummary);

  // Count comes from the response pagination summary, not flatData.length.
  const totalCount = query.data?.pages?.[0]?.pagination?.totalSize;

  const renderContent = () => {
    if (query.isInitialLoading) {
      return <TablePayrolls items={[]} loading />;
    }

    if (query.isError) {
      return (
        <WSEmptyState
          actions={{
            buttons: [{ label: "Try again", onClick: () => query.refetch() }]
          }}
          description="Something went wrong loading payroll runs."
          title="Couldn't load payroll history"
          type="error"
        />
      );
    }

    if (payrollRuns.length === 0) {
      return cycleType ? (
        <WSEmptyState
          description="No payroll runs match the selected filters."
          title="No matching payroll runs"
          type="search"
        />
      ) : (
        <WSEmptyState
          description="Completed payroll runs for the selected year will appear here."
          title="No payroll history yet"
          type="documents"
        />
      );
    }

    return (
      <WSInfiniteScroll
        endOfList={!query.hasNextPage}
        loading={!!query.isFetchingNextPage}
        loadMore={payrollRuns.length > 0}
        onLoad={() => query.fetchNextPage()}
      >
        <TablePayrolls items={payrollRuns} onRowOpen={props.onRowOpen} />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="L">
      <WSFilters<PayrollsFilter>
        info={totalCount === undefined ? undefined : { count: totalCount }}
        primaryFilters={filtersConfig}
        values={urlQuery.filter}
        onFilter={newFilter =>
          setUrlQuery(prev => ({ ...prev, filter: newFilter }))
        }
      />
      {renderContent()}
    </WSList>
  );
};
